import React, { useEffect, useState } from 'react';
import { Loader2, ArrowLeft } from 'lucide-react';

import SafeHtml from './SafeHtml';
const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Public legal page renderer. Consumes /api/config/legal/{slug} and renders
 * the admin-edited HTML (Quill output). Linked from the registration form
 * checkboxes — opens in a new tab.
 */
export default function LegalPage({ slug }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/config/legal/${slug}`);
        if (!r.ok) throw new Error(await r.text());
        const data = await r.json();
        if (alive) setDoc(data);
      } catch (e) {
        if (alive) setError(e.message || 'Failed to load');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-4" data-testid={`legal-page-${slug}`}>
      <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white px-8 py-6 flex items-center gap-4">
          <button
            type="button"
            onClick={() => window.history.length > 1 ? window.history.back() : (window.location.href = '/')}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-sm"
            data-testid="legal-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <h1 className="text-2xl font-bold">{doc?.title || 'Legal'}</h1>
        </div>

        <div className="px-8 py-8">
          {loading && (
            <p className="text-center text-slate-500 py-12">
              <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading…
            </p>
          )}
          {error && !loading && (
            <p className="text-center text-red-600 py-12">{error}</p>
          )}
          {doc && !loading && !error && (
            <SafeHtml as="article" className="legal-content prose prose-slate max-w-none"
              data-testid={`legal-content-${slug}`} html={doc.html} />
          )}
          {doc?.updated_at && (
            <p className="mt-8 pt-4 border-t border-slate-100 text-xs text-slate-400">
              Last updated: {new Date(doc.updated_at).toLocaleString()}
            </p>
          )}
        </div>
      </div>

      {/* Tailwind doesn't include @tailwindcss/typography by default — ship a
          minimal stylesheet so admin Quill output renders nicely. */}
      <style>{`
        .legal-content h1, .legal-content h2, .legal-content h3 { font-weight: 700; color: #0f172a; }
        .legal-content h1 { font-size: 1.875rem; margin: 1.5rem 0 1rem; }
        .legal-content h2 { font-size: 1.5rem; margin: 1.25rem 0 0.75rem; }
        .legal-content h3 { font-size: 1.25rem; margin: 1rem 0 0.5rem; }
        .legal-content p  { margin: 0.5rem 0; line-height: 1.7; color: #334155; }
        .legal-content ul { list-style: disc; padding-left: 1.5rem; margin: 0.5rem 0; }
        .legal-content ol { list-style: decimal; padding-left: 1.5rem; margin: 0.5rem 0; }
        .legal-content li { margin: 0.25rem 0; }
        .legal-content a  { color: #0891b2; text-decoration: underline; }
        .legal-content strong { font-weight: 700; color: #0f172a; }
        .legal-content em { font-style: italic; }
      `}</style>
    </div>
  );
}
